import { EntityDefaultName, TemplateRawForm } from '@hevoio/hevo-ui-toolkit';
import { GenericApiResponse } from '../../shared/interfaces/generics';
import { hermesConfig } from '../../request/hermes.config';
import { RawDestination, RawDestinationType } from '../interfaces/destination';
import {
  BACKGROUND_NETWORK_REQ_UI_OPTIONS,
  DEFAULT_NETWORK_REQ_OPTIONS
} from '../../request/constants/request-options';
import { NetworkRequestOptions } from '../../request/interfaces/request-options';
import { AsyncTaskStatusResponse, AsyncTaskResponse } from '../../shared/interfaces/async-task';
import { RawEntityDisplayConfig } from '../../shared/interfaces/entity-display-config';
import { RawIntegration } from '../../pipelines/interfaces/integration';
import { hermesCourierService } from '../../request/services/hermes.courier.service';

export const DestinationAPI = {
  destinationsURL: hermesConfig.getDestinationsURL(),
  taskFlowURL: hermesConfig.getTaskFlowURL(),

  getDestinationTypes(options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.destinationsURL}/types`;

    return hermesCourierService.get<GenericApiResponse<RawDestinationType[]>>(requestURL, options);
  },

  getDestinations(options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = this.destinationsURL;

    return hermesCourierService.get<GenericApiResponse<RawDestination[]>>(requestURL, {
      ...options,
      networkOptions: {
        params: {
          include_deleted: false
        }
      }
    });
  },

  createDestination(config: object, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.destinationsURL}/create`;

    return hermesCourierService.post<GenericApiResponse<RawDestination>>(requestURL, config, options);
  },

  getDestination(destinationId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.destinationsURL}/${destinationId}`;

    return hermesCourierService.get<GenericApiResponse<RawDestination>>(requestURL, options);
  },

  validateDestinationName(destinationName: string, config = {}) {
    const requestURL = `${this.destinationsURL}/validate/destination-name`;

    const params = {
      destination_name: destinationName
    };

    const options: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS,
      networkOptions: {
        ...config
      }
    };

    return hermesCourierService.post<GenericApiResponse<{ valid: boolean; reason?: string }>>(
      requestURL,
      params,
      options
    );
  },

  getDestinationIntegrations(destinationId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.destinationsURL}/${destinationId}/integrations`;

    return hermesCourierService.get<GenericApiResponse<RawIntegration[]>>(requestURL, {
      ...options,
      networkOptions: {
        params: {
          include_deleted: false
        }
      }
    });
  },

  deleteDestination(destinationId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.destinationsURL}/${destinationId}`;

    return hermesCourierService.delete<GenericApiResponse<RawDestination>>(requestURL, options);
  },

  getDestinationConfigTemplateByType(
    destinationType: string,
    standardDestinationId?: number,
    options = DEFAULT_NETWORK_REQ_OPTIONS
  ) {
    const requestURL = `${this.destinationsURL}/${destinationType}/config-template`;

    return hermesCourierService.get<GenericApiResponse<TemplateRawForm>>(requestURL, {
      ...options,
      networkOptions: {
        ...options.networkOptions,
        params: {
          standard_destination_id: standardDestinationId
        }
      }
    });
  },

  getDestinationConfigTemplateWithFormData(
    destinationId: number,
    options = DEFAULT_NETWORK_REQ_OPTIONS
  ) {
    const requestURL = `${this.destinationsURL}/${destinationId}/config/edit`;

    return hermesCourierService.get<GenericApiResponse<TemplateRawForm>>(requestURL, options);
  },

  getDefaultDestinationName(
    destinationType: string,
    options = DEFAULT_NETWORK_REQ_OPTIONS
  ): Promise<GenericApiResponse<EntityDefaultName>> {
    const requestURL = `${this.destinationsURL}/${destinationType}/default-name`;

    return hermesCourierService.get<GenericApiResponse<EntityDefaultName>>(requestURL, options);
  },

  getTestConnectionTaskIdForNewDestination(
    destinationType: string,
    params: unknown
  ): Promise<GenericApiResponse<AsyncTaskResponse>> {
    const requestUrl = `${this.destinationsURL}/test`;

    const payload = {
      destination_type: destinationType,
      ...(params as object)
    };

    return hermesCourierService.post<GenericApiResponse<AsyncTaskResponse>>(requestUrl, payload);
  },

  getTestConnectionTaskIdForExistingDestination(
    destinationId: number
  ): Promise<GenericApiResponse<AsyncTaskResponse>> {
    const requestUrl = `${this.destinationsURL}/${destinationId}/test`;

    return hermesCourierService.post<GenericApiResponse<AsyncTaskResponse>>(requestUrl, {});
  },

  getTestConnectionTaskIdForDestinationEdit(destinationId: number, destinationConfig: object) {
    const requestUrl = `${this.destinationsURL}/${destinationId}/edit/test`;

    return hermesCourierService.post<GenericApiResponse<AsyncTaskResponse>>(
      requestUrl,
      destinationConfig
    );
  },

  getTestConnectionStatus(
    taskFlowId: string
  ): Promise<GenericApiResponse<AsyncTaskStatusResponse>> {
    const requestUrl = `${this.taskFlowURL}/${taskFlowId}/status`;

    return hermesCourierService.get<GenericApiResponse<AsyncTaskStatusResponse>>(requestUrl);
  },

  getDestinationDisplayConfigDetails(destinationId: number) {
    const requestUrl = `${this.destinationsURL}/${destinationId}/display-config`;

    return hermesCourierService.get<GenericApiResponse<RawEntityDisplayConfig<RawDestinationType>>>(
      requestUrl
    );
  },

  editDestination(destinationId: number, destinationConfig: object) {
    const requestUrl = `${this.destinationsURL}/${destinationId}/config/edit`;

    return hermesCourierService.post<GenericApiResponse<RawDestination>>(requestUrl, destinationConfig);
  }
};
